import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Shield, CheckCircle, Eye, EyeOff, ArrowLeft, Zap } from 'lucide-react';

function ChangePasswordSection({ setCurrentPage, isMobile, user }) {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));

    // Calculate password strength for new password
    if (name === 'newPassword') {
      let strength = 0;
      if (value.length >= 8) strength += 25;
      if (/[A-Z]/.test(value)) strength += 25;
      if (/[0-9]/.test(value)) strength += 25;
      if (/[^A-Za-z0-9]/.test(value)) strength += 25;
      setPasswordStrength(strength);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match!');
      setIsSubmitting(false);
      return;
    }
    if (passwordData.newPassword.length < 8) {
      alert('New password must be at least 8 characters long!');
      setIsSubmitting(false);
      return;
    }

    // Simulate API call
    setTimeout(() => {
      alert('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStrength(0);
      setIsSubmitting(false);
    }, 1500);
  };

  const getStrengthColor = () => {
    if (passwordStrength >= 75) return 'from-emerald-500 to-green-500';
    if (passwordStrength >= 50) return 'from-amber-500 to-orange-500';
    if (passwordStrength >= 25) return 'from-yellow-500 to-amber-500';
    return 'from-red-500 to-rose-500';
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-red-500 to-pink-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            Security Settings
          </h2>
          <p className="text-gray-400">Update your password to keep your account secure</p>
        </div>
        
        <button 
          onClick={() => setCurrentPage('dashboard')}
          className="px-4 py-2 border border-gray-700 text-gray-300 hover:bg-gray-800 rounded-lg transition-colors flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
      </div>

      {/* Main Form */}
      <div className="relative">
        {/* Glow Effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-red-500/10 via-purple-500/10 to-blue-500/10 rounded-3xl blur-xl"></div>
        
        {/* Form Container */}
        <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-gray-800 p-8 shadow-2xl">
          {/* Security Status */}
          <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-gray-900 to-black border border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Security Status: Excellent</h3>
                  <p className="text-gray-400">Your account is protected with the latest security measures</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-400">Last updated</div>
                <div className="text-white font-bold">Today</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Current Password */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-300">
                Current Password <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPasswords.current ? "text" : "password"}
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all pl-12"
                  placeholder="Enter your current password"
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <Lock className="w-5 h-5 text-gray-500" />
                </div>
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* New Password with Strength Meter */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-300">
                New Password <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPasswords.new ? "text" : "password"}
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all pl-12"
                  placeholder="Enter your new password"
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <Shield className="w-5 h-5 text-gray-500" />
                </div>
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* Password Strength Meter */}
              {passwordData.newPassword && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">Password Strength</span>
                    <span className="text-gray-300">{passwordStrength}%</span>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${passwordStrength}%` }}
                      className={`h-full bg-gradient-to-r ${getStrengthColor()}`}
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {[
                      { label: '8+ characters', met: passwordData.newPassword.length >= 8 },
                      { label: 'Uppercase', met: /[A-Z]/.test(passwordData.newPassword) },
                      { label: 'Number', met: /[0-9]/.test(passwordData.newPassword) },
                      { label: 'Special', met: /[^A-Za-z0-9]/.test(passwordData.newPassword) }
                    ].map((req, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${req.met ? 'bg-emerald-500' : 'bg-gray-700'}`} />
                        <span className={req.met ? 'text-emerald-400' : 'text-gray-500'}>{req.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-300">
                Confirm New Password <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? "text" : "password"}
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all pl-12"
                  placeholder="Confirm your new password"
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <CheckCircle className="w-5 h-5 text-gray-500" />
                </div>
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password Match Indicator */}
              {passwordData.newPassword && passwordData.confirmPassword && (
                <div className={`flex items-center gap-2 text-sm ${passwordData.newPassword === passwordData.confirmPassword ? 'text-emerald-400' : 'text-red-400'}`}>
                  {passwordData.newPassword === passwordData.confirmPassword ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <EyeOff className="w-4 h-4" />
                  )}
                  <span>
                    {passwordData.newPassword === passwordData.confirmPassword 
                      ? 'Passwords match' 
                      : 'Passwords do not match'}
                  </span>
                </div>
              )}
            </div>

            {/* Security Tips */}
            <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
              <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                Security Tips
              </h4>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• Use a combination of letters, numbers, and symbols</li>
                <li>• Avoid using personal information in your password</li>
                <li>• Consider using a password manager</li>
                <li>• Never share your password with anyone</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t border-gray-800">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`px-8 py-4 rounded-xl font-bold text-lg shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 ${
                  isSubmitting
                    ? 'bg-gray-700 cursor-not-allowed'
                    : 'bg-gradient-to-r from-red-600 to-pink-600 hover:shadow-red-500/40'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Updating Password...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    Change Password
                  </>
                )}
              </motion.button>

              <button
                type="button"
                onClick={() => {
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                  setPasswordStrength(0);
                }}
                className="px-8 py-4 border border-gray-700 text-gray-300 hover:bg-gray-800/50 rounded-xl font-bold transition-colors"
              >
                Clear Form
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Additional Security Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          {
            title: "Two-Factor Authentication",
            description: "Add an extra layer of security to your account",
            status: "Not Enabled",
            action: "Enable 2FA",
            color: "from-blue-500 to-cyan-500",
            icon: "🔐"
          },
          {
            title: "Login Notifications",
            description: "Get alerts for new sign-ins",
            status: "Enabled",
            action: "Manage",
            color: "from-green-500 to-emerald-500",
            icon: "📱"
          },
          {
            title: "Active Sessions",
            description: "Manage your logged-in devices",
            status: "3 Active",
            action: "View All",
            color: "from-purple-500 to-pink-500",
            icon: "💻"
          }
        ].map((option, index) => (
          <div key={index} className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6 hover:border-gray-700 transition-colors">
            <div className="flex items-center gap-4 mb-4">
              <div className={`text-2xl bg-gradient-to-r ${option.color} bg-clip-text text-transparent`}>
                {option.icon}
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-white">{option.title}</h4>
                <div className="text-sm text-gray-400">{option.description}</div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-xs px-3 py-1 rounded-full ${option.status.includes('Enabled') || option.status.includes('Active') ? 'bg-green-500/20 text-green-400' : 'bg-gray-800 text-gray-400'}`}>
                {option.status}
              </span>
              <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                {option.action}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ChangePasswordSection;